// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react'
import { Badge, Button, Card, Toggle } from '@/components/ui'
import { Modal } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { ThemePreview } from '@/components/properties/PropertyMicrositeFields'
import {
  useMicrositePages,
  useCreateMicrositePage,
  useUpdateMicrositePage,
  useDeleteMicrositePage,
} from '@/hooks/useMicrosite'
import type { MicrositePage, MicrositePageType, MicrositePageContent } from '@/hooks/useMicrosite'
import { MicrositePageEditor } from './MicrositePageEditor'
import { Spinner } from '@/components/ui/Spinner'

const DEFAULT_PAGE_TYPES: MicrositePageType[] = ['home', 'room', 'contact']

const DEFAULT_PAGE_DEFS: Array<{
  title: string
  slug: string
  page_type: MicrositePageType
  sort_order: number
}> = [
  { title: 'Home', slug: 'home', page_type: 'home', sort_order: 0 },
  { title: 'Room', slug: 'room/1', page_type: 'room', sort_order: 1 },
  { title: 'Contact', slug: 'contact', page_type: 'contact', sort_order: 2 },
]

function isDefaultPage(page: MicrositePage): boolean {
  return DEFAULT_PAGE_TYPES.includes(page.page_type)
}

interface PageTypeBadgeProps {
  pageType: MicrositePageType
}

function PageTypeBadge({ pageType }: PageTypeBadgeProps) {
  const { t } = useTranslation('microsite')

  const variantMap: Record<MicrositePageType, 'primary' | 'secondary' | 'accent' | 'info' | 'ghost'> = {
    home: 'primary',
    about: 'secondary',
    contact: 'accent',
    rooms: 'info',
    room: 'info',
    custom: 'ghost',
  }

  return (
    <Badge size="sm" variant={variantMap[pageType]}>
      {t(`pages.page_types.${pageType}`)}
    </Badge>
  )
}

interface MicrositePageListProps {
  propertyId: number
  theme?: import('@/hooks/useMicrosite').MicrositeTheme
  template?: string | null
  propertyName?: string
}

export function MicrositePageList({ propertyId, theme: parentTheme, template: parentTemplate, propertyName }: MicrositePageListProps) {
  const { t } = useTranslation('microsite')
  const { t: tc } = useTranslation('common')

  const { data: pages, isLoading } = useMicrositePages(propertyId)
  const createPage = useCreateMicrositePage()
  const updatePage = useUpdateMicrositePage()
  const deletePage = useDeleteMicrositePage()

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingPage, setEditingPage] = useState<MicrositePage | null>(null)
  const [editingPageType, setEditingPageType] = useState<MicrositePageType>('home')
  const [editingContent, setEditingContent] = useState<MicrositePageContent>({})
  const [deleteTarget, setDeleteTarget] = useState<MicrositePage | null>(null)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; key: string } | null>(null)
  const autoCreatedRef = useRef(false)

  const showNotification = (type: 'success' | 'error', key: string) => {
    setNotification({ type, key })
    setTimeout(() => setNotification(null), 3000)
  }

  const sortedPages = [...(pages ?? [])].sort((a, b) => a.sort_order - b.sort_order)

  // Auto-create default pages when pages list is empty
  useEffect(() => {
    if (isLoading || autoCreatedRef.current) return
    if (pages && pages.length === 0) {
      autoCreatedRef.current = true
      ;(async () => {
        try {
          for (const def of DEFAULT_PAGE_DEFS) {
            await createPage.mutateAsync({
              propertyId,
              title: def.title,
              slug: def.slug,
              page_type: def.page_type,
              sort_order: def.sort_order,
              is_published: true,
            })
          }
        } catch {
          // ignore — pages may already exist
        }
      })()
    }
  }, [pages, isLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenAdd = () => {
    setEditingPage(null)
    setEditingPageType('room')
    setEditingContent({})
    setEditorOpen(true)
  }

  const handleOpenEdit = (page: MicrositePage) => {
    setEditingPage(page)
    setEditingPageType(page.page_type)
    setEditingContent(page.content ?? {})
    setEditorOpen(true)
  }

  const handleCloseEditor = () => {
    setEditorOpen(false)
    setEditingPage(null)
    setEditingContent({})
  }

  const handleSave = async (formData: {
    title: string
    slug: string
    page_type: MicrositePageType
    content: MicrositePageContent
    is_published: boolean
    show_in_nav: boolean
  }) => {
    try {
      if (editingPage) {
        await updatePage.mutateAsync({
          propertyId,
          id: editingPage.id,
          title: formData.title,
          slug: formData.slug,
          page_type: formData.page_type,
          content: formData.content,
          is_published: formData.is_published,
          show_in_nav: formData.show_in_nav,
        })
        showNotification('success', 'pages.messages.update_success')
      } else {
        const maxSortOrder = sortedPages.length > 0 ? sortedPages[sortedPages.length - 1].sort_order + 1 : 0
        await createPage.mutateAsync({
          propertyId,
          title: formData.title,
          slug: formData.slug,
          page_type: formData.page_type,
          content: formData.content,
          is_published: formData.is_published,
          show_in_nav: formData.show_in_nav,
          sort_order: maxSortOrder,
        })
        showNotification('success', 'pages.messages.create_success')
      }
      handleCloseEditor()
    } catch {
      showNotification('error', editingPage ? 'pages.messages.update_error' : 'pages.messages.create_error')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deletePage.mutateAsync({ propertyId, id: deleteTarget.id })
      showNotification('success', 'pages.messages.delete_success')
    } catch {
      showNotification('error', 'pages.messages.delete_error')
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleTogglePublished = async (page: MicrositePage) => {
    try {
      await updatePage.mutateAsync({
        propertyId,
        id: page.id,
        is_published: !page.is_published,
      })
    } catch {
      showNotification('error', 'pages.messages.update_error')
    }
  }

  const handleMove = async (page: MicrositePage, direction: 'up' | 'down') => {
    const idx = sortedPages.findIndex((p) => p.id === page.id)
    if (direction === 'up' && idx === 0) return
    if (direction === 'down' && idx === sortedPages.length - 1) return

    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    const swapPage = sortedPages[swapIdx]

    try {
      await Promise.all([
        updatePage.mutateAsync({ propertyId, id: page.id, sort_order: swapPage.sort_order }),
        updatePage.mutateAsync({ propertyId, id: swapPage.id, sort_order: page.sort_order }),
      ])
    } catch {
      showNotification('error', 'pages.messages.update_error')
    }
  }

  if (isLoading) {
    return (
      <Card title={t('pages.section_title')}>
        <div className="flex items-center justify-center h-20">
          <Spinner size="md" className="text-primary" />
        </div>
      </Card>
    )
  }

  return (
    <>
      <Card title={t('pages.section_title')}>
        {/* Notification banner */}
        {notification && (
          <Alert
            variant={notification.type as 'info' | 'success' | 'warning' | 'error'}
            icon={null}
            role={notification.type === 'error' ? 'alert' : 'status'}
            className="text-sm mb-4"
          >
            <span>{t(notification.key)}</span>
          </Alert>
        )}

        {sortedPages.length === 0 ? (
          /* Empty state — auto-create is in progress */
          <div className="flex items-center justify-center h-20">
            <Spinner size="md" className="text-primary" />
          </div>
        ) : (
          /* Page list */
          <div className="space-y-3">
            <div className="divide-y divide-base-200 rounded-lg border border-base-200">
              {sortedPages.map((page, idx) => (
                <div
                  key={page.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-base-200"
                  data-testid={`page-row-${page.id}`}
                >
                  {/* Reorder arrows */}
                  <div className="flex flex-col gap-0.5 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="p-0 h-5 min-h-0 w-5"
                      onClick={() => handleMove(page, 'up')}
                      disabled={idx === 0 || updatePage.isPending}
                      aria-label={t('pages.move_up', { title: page.title })}
                    >
                      <ChevronUp size={14} aria-hidden="true" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      className="p-0 h-5 min-h-0 w-5"
                      onClick={() => handleMove(page, 'down')}
                      disabled={idx === sortedPages.length - 1 || updatePage.isPending}
                      aria-label={t('pages.move_down', { title: page.title })}
                    >
                      <ChevronDown size={14} aria-hidden="true" />
                    </Button>
                  </div>

                  {/* Title + slug + type */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm truncate">{page.title}</span>
                      <PageTypeBadge pageType={page.page_type} />
                    </div>
                    <span className="text-xs text-base-content/50 font-mono">/{page.slug}</span>
                  </div>

                  {/* Published toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer shrink-0">
                    <Toggle
                      variant="primary"
                      className="toggle-xs"
                      checked={page.is_published}
                      onChange={() => handleTogglePublished(page)}
                      aria-label={t(page.is_published ? 'pages.unpublish_page' : 'pages.publish_page', { title: page.title })}
                    />
                    <span className="text-xs text-base-content/60 hidden sm:inline">
                      {t('status.' + (page.is_published ? 'published' : 'unpublished'))}
                    </span>
                  </label>

                  {/* Edit / Delete */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => handleOpenEdit(page)}
                      aria-label={`${t('pages.edit_page')}: ${page.title}`}
                    >
                      <Pencil size={14} aria-hidden="true" />
                    </Button>
                    {!isDefaultPage(page) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className="text-error"
                        onClick={() => setDeleteTarget(page)}
                        aria-label={`${t('pages.delete_page')}: ${page.title}`}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Add page button */}
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Plus size={14} aria-hidden="true" />}
                onClick={handleOpenAdd}
                aria-label={t('pages.add_page')}
              >
                {t('pages.add_page')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Full-screen page editor */}
      {editorOpen && (
        <div className="fixed inset-0 z-50 bg-base-100 flex flex-col">
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-base-300 shrink-0">
            <h2 className="text-lg font-semibold">{editingPage ? t('pages.edit_page') : t('pages.add_page')}</h2>
            <Button type="button" variant="ghost" size="sm" shape="square" aria-label={tc('actions.close')} onClick={handleCloseEditor}>
              <X size={18} />
            </Button>
          </div>
          {/* Two-column: form + preview */}
          <div className="flex-1 flex overflow-hidden">
            <div className="w-1/2 overflow-y-auto p-6 border-r border-base-300">
              <MicrositePageEditor
                page={editingPage}
                defaultPageType={editingPageType}
                onSave={handleSave}
                onCancel={handleCloseEditor}
                isSaving={createPage.isPending || updatePage.isPending}
                onContentChange={setEditingContent}
                onPageTypeChange={setEditingPageType}
              />
            </div>
            <div className="w-1/2 overflow-y-auto p-6 bg-base-200/50">
              {parentTheme && propertyName ? (
                <ThemePreview
                  theme={parentTheme}
                  template={(parentTemplate as 'modern' | 'classic' | 'elegant') ?? null}
                  propertyName={propertyName}
                  lockedPage={
                    editingPageType === 'home' ? 'landing'
                      : editingPageType === 'room' || editingPageType === 'rooms' ? 'rooms'
                      : editingPageType === 'contact' ? 'contact'
                      : 'landing'
                  }
                  pageContent={editingContent}
                />
              ) : (
                <div className="flex items-center justify-center h-full text-base-content/40 text-sm">
                  {t('preview.title')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <Modal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title={t('pages.delete_page')}
          size="sm"
        >
          <p className="text-sm text-base-content/80 mb-6">
            {t('pages.messages.delete_confirm', { title: deleteTarget.title })}
          </p>
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              disabled={deletePage.isPending}
            >
              {tc('actions.cancel')}
            </Button>
            <Button
              type="button"
              variant="error"
              onClick={handleDelete}
              loading={deletePage.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  )
}
